<h5 class="mb-3 fw-bold">Utility Rates</h5>

@if (isLoading) {
  <div class="d-flex align-items-center">
    <div class="spinner-border spinner-border-sm me-2" role="status">
      <span class="visually-hidden">Loading...</span>
    </div>
    <span>Loading rates...</span>
  </div>
} @else {
  <!-- Demand Rates Table -->
  <app-editable-table-section
    sectionTitle="Demand Rates"
    [expanded]="showDemandRates()"
    [addTopBorder]="false"
    [showEditButton]="!readOnly"
    [editing]="editingDemandRates()"
    [isSaving]="isSavingDemandRates"
    [saveDisabled]="!isDemandRatesEditValid()"
    editButtonTitle="Edit demand rates"
    [hasRows]="(editingDemandRates() ? demandRatesEdit().length : demandRates.length) > 0"
    [hasRowsMarkedForDelete]="demandRatesToDelete().length > 0"
    emptyMessage="No rows. Click &quot;Add row&quot; to add a demand rate."
    [viewEmptyMessage]="'No demand rates found for this schedule.'"
    allRowsDeletedMessage="All rows marked for deletion. Click Save to confirm, or Cancel to undo."
    (toggle)="toggleDemandRates()"
    (enterEdit)="enterDemandRatesEdit()"
    (cancelEdit)="cancelDemandRatesEdit()"
    (addRow)="addDemandRateRow()"
    (save)="saveDemandRatesEdit()">
    <div editContent>
        <div class="table-responsive">
          <table class="table table-sm table-striped">
            <thead>
              <tr>
                <th>Rate Description</th>
                <th>Season</th>
                <th>Rate ($/kW)</th>
                <th>Min kV</th>
                <th>Max kV</th>
                <th>kW Range</th>
                <th>Start Time</th>
                <th>End Time</th>
                <th>Applies</th>
                <th class="text-start" style="width: 3.5rem;" aria-label="Actions"></th>
              </tr>
            </thead>
            <tbody>
              @for (rate of demandRatesEdit(); track $index) {
                <tr>
                  <td>
                    <input
                      type="text"
                      class="form-control form-control-sm"
                      [class.is-invalid]="!isRateDescriptionValid(rate)"
                      [value]="rate.rate_description ?? ''"
                      (input)="updateDemandRateField($index, 'rate_description', $any($event.target).value)"
                      placeholder="Description (required)" />
                    @if (!isRateDescriptionValid(rate)) {
                      <div class="invalid-feedback d-block">Rate description is required.</div>
                    }
                  </td>
                  <td class="season-cell">
                    <select
                      class="form-select form-select-sm mb-1"
                      [ngModel]="getSeasonType(rate)"
                      (ngModelChange)="setSeasonType($index, $event)">
                      <option [value]="seasonTypeAllYear">All year</option>
                      <option [value]="seasonTypeDateRange">Date range</option>
                    </select>
                    @if (getSeasonType(rate) === seasonTypeDateRange) {
                      <input
                        type="text"
                        class="form-control form-control-sm mb-1"
                        [value]="rate.season_description ?? ''"
                        (input)="updateDemandRateField($index, 'season_description', $any($event.target).value)"
                        placeholder="e.g. Summer, Winter" />
                      @if (expandedSeasonRowIndex() === $index) {
                        <div class="mb-1">
                          <app-datepicker
                            [ngModel]="getSeasonRange($index)"
                            (ngModelChange)="onSeasonRangeChange($index, $event)"
                            [range]="true"
                            [displayMonths]="2"
                            [hideNavigation]="false" />
                          <button
                            type="button"
                            class="btn btn-outline-secondary btn-sm mt-1"
                            (click)="toggleSeasonDatepickerExpanded($index)">
                            Done
                          </button>
                        </div>
                      } @else {
                        <div class="d-flex align-items-center gap-1 flex-wrap">
                          <span class="text-muted small">{{ formatSeasonRangeDisplay(rate) }}</span>
                          <button
                            type="button"
                            class="btn btn-outline-secondary btn-sm py-0"
                            (click)="toggleSeasonDatepickerExpanded($index)">
                            {{ getSeasonRange($index)?.fromDate && getSeasonRange($index)?.toDate ? 'Change dates' : 'Select dates' }}
                          </button>
                        </div>
                      }
                    }
                  </td>
                  <td>
                    <input
                      type="number"
                      class="form-control form-control-sm"
                      [class.is-invalid]="!isRateValueValid(rate)"
                      [value]="rate.demand_charge_rate_kw ?? ''"
                      (input)="updateDemandRateField($index, 'demand_charge_rate_kw', $any($event.target).value)"
                      placeholder="0 (required)"
                      step="0.01" />
                    @if (!isRateValueValid(rate)) {
                      <div class="invalid-feedback d-block">Rate is required (positive or negative number).</div>
                    }
                  </td>
                  <td>
                    <input
                      type="number"
                      class="form-control form-control-sm"
                      [value]="rate.min_kv ?? ''"
                      (input)="updateDemandRateField($index, 'min_kv', $any($event.target).value)"
                      placeholder="-"
                      step="0.1"
                      min="0" />
                  </td>
                  <td>
                    <input
                      type="number"
                      class="form-control form-control-sm"
                      [value]="rate.max_kv ?? ''"
                      (input)="updateDemandRateField($index, 'max_kv', $any($event.target).value)"
                      placeholder="-"
                      step="0.1"
                      min="0" />
                  </td>
                  <td>
                    <div class="d-flex align-items-center gap-1">
                      <input
                        type="number"
                        class="form-control form-control-sm"
                        [value]="rate.start_kw ?? ''"
                        (input)="updateDemandRateField($index, 'start_kw', $any($event.target).value)"
                        placeholder="Start"
                        step="0.01"
                        min="0" />
                      <span class="text-muted">-</span>
                      <input
                        type="number"
                        class="form-control form-control-sm"
                        [value]="rate.end_kw ?? ''"
                        (input)="updateDemandRateField($index, 'end_kw', $any($event.target).value)"
                        placeholder="End"
                        step="0.01"
                        min="0" />
                    </div>
                  </td>
                  <td>
                    <input
                      type="text"
                      class="form-control form-control-sm"
                      [value]="rate.start_time ?? ''"
                      (input)="updateDemandRateField($index, 'start_time', $any($event.target).value)"
                      placeholder="HH:MM" />
                  </td>
                  <td>
                    <input
                      type="text"
                      class="form-control form-control-sm"
                      [value]="rate.end_time ?? ''"
                      (input)="updateDemandRateField($index, 'end_time', $any($event.target).value)"
                      placeholder="HH:MM" />
                  </td>
                  <td>
                    <select
                      class="form-select form-select-sm"
                      [ngModel]="getAppliesValue(rate.days_of_week_holidays)"
                      (ngModelChange)="updateDemandRateField($index, 'days_of_week_holidays', $event)">
                      <option value="">—</option>
                      @for (opt of appliesOptions; track opt.value) {
                        <option [value]="opt.value">{{ opt.label }}</option>
                      }
                    </select>
                  </td>
                  <td class="text-start">
                    <div class="d-flex align-items-center justify-content-start gap-1">
                      <button
                        type="button"
                        class="btn btn-outline-danger btn-sm py-0 px-1"
                        (click)="deleteDemandRateRow($index)"
                        title="Delete row">
                        <i class="bi bi-trash" aria-label="Delete row"></i>
                      </button>
                      @if (rate.manually_entered) {
                        <i class="bi bi-pencil-square text-secondary" title="Manually entered" aria-label="Manually entered"></i>
                      }
                    </div>
                  </td>
                </tr>
              }
            </tbody>
            </table>
        </div>
    </div>
    <div viewContent>
        @if (demandRates.length > 0) {
          <div class="table-responsive">
            <table class="table table-sm table-striped">
              <thead>
                <tr>
                  <th>Rate Description</th>
                  <th>Season</th>
                  <th>Rate ($/kW)</th>
                  <th>Min/Max kV</th>
                  <th>kW Range</th>
                  <th>Time Period</th>
                  <th>Applies</th>
                  <th class="text-end" style="width: 3.5rem;" aria-label="Manually entered"></th>
                </tr>
              </thead>
              <tbody>
                @for (seasonGroup of demandRatesBySeason; track seasonGroup.season) {
                  @for (rate of seasonGroup.rates; track rate.id) {
                    @let seasonTooltip = getSeasonTooltipText(rate);
                    <tr>
                      <td>{{ rate.rate_description || '-' }}</td>
                      <td>
                        <span>{{ rate.season_description || '-' }}</span>
                        @if (seasonTooltip) {
                          <span class="season-info-icon ms-1">
                            <button type="button" class="season-info-button" aria-label="Show season date range">
                              <i class="bi bi-info-circle"></i>
                              <span class="season-tooltip" role="tooltip">{{ seasonTooltip }}</span>
                            </button>
                          </span>
                        }
                      </td>
                      <td>{{ rate.demand_charge_rate_kw || '-' }}</td>
                      <td>
                        @if (rate.min_kv || rate.max_kv) {
                          {{ rate.min_kv || 'N/A' }} - {{ rate.max_kv || 'N/A' }}
                        } @else {
                          -
                        }
                      </td>
                      <td>{{ getDemandRangeText(rate) }}</td>
                      <td>
                        @if (rate.start_time || rate.end_time) {
                          {{ rate.start_time || 'N/A' }} - {{ rate.end_time || 'N/A' }}
                        } @else {
                          -
                        }
                      </td>
                      <td>{{ getAppliesText(rate.days_of_week_holidays) }}</td>
                      <td class="text-end">
                        @if (rate.manually_entered) {
                          <i class="bi bi-pencil-square text-secondary" title="Manually entered" aria-label="Manually entered"></i>
                        }
                      </td>
                    </tr>
                  }
                }
              </tbody>
            </table>
          </div>
        }
    </div>
  </app-editable-table-section>

  <!-- Energy Rates Table -->
  <app-editable-table-section
    sectionTitle="Energy Rates"
    [expanded]="showEnergyRates()"
    [showEditButton]="!readOnly"
    [editing]="editingEnergyRates()"
    [isSaving]="isSavingEnergyRates"
    [saveDisabled]="!isEnergyRatesEditValid()"
    editButtonTitle="Edit energy rates"
    [hasRows]="(editingEnergyRates() ? energyRatesEdit().length : energyRates.length) > 0"
    [hasRowsMarkedForDelete]="energyRatesToDelete().length > 0"
    emptyMessage="No rows. Click &quot;Add row&quot; to add an energy rate."
    [viewEmptyMessage]="'No energy rates found for this schedule.'"
    allRowsDeletedMessage="All rows marked for deletion. Click Save to confirm, or Cancel to undo."
    (toggle)="toggleEnergyRates()"
    (enterEdit)="enterEnergyRatesEdit()"
    (cancelEdit)="cancelEnergyRatesEdit()"
    (addRow)="addEnergyRateRow()"
    (save)="saveEnergyRatesEdit()">
    <div editContent>
        <div class="table-responsive">
          <table class="table table-sm table-striped">
            <thead>
              <tr>
                <th>Rate Description</th>
                <th>Season</th>
                <th>Rate ($/kWh)</th>
                <th>Min kV</th>
                <th>Max kV</th>
                <th>kWh Range</th>
                <th>Start Time</th>
                <th>End Time</th>
                <th>Applies</th>
                <th class="text-start" style="width: 3.5rem;" aria-label="Actions"></th>
              </tr>
            </thead>
            <tbody>
              @for (rate of energyRatesEdit(); track $index) {
                <tr>
                  <td>
                    <input
                      type="text"
                      class="form-control form-control-sm"
                      [class.is-invalid]="!isRateDescriptionValid(rate)"
                      [value]="rate.rate_description ?? ''"
                      (input)="updateEnergyRateField($index, 'rate_description', $any($event.target).value)"
                      placeholder="Description (required)" />
                    @if (!isRateDescriptionValid(rate)) {
                      <div class="invalid-feedback d-block">Rate description is required.</div>
                    }
                  </td>
                  <td class="season-cell">
                    <select
                      class="form-select form-select-sm mb-1"
                      [ngModel]="getSeasonType(rate)"
                      (ngModelChange)="setEnergySeasonType($index, $event)">
                      <option [value]="seasonTypeAllYear">All year</option>
                      <option [value]="seasonTypeDateRange">Date range</option>
                    </select>
                    @if (getSeasonType(rate) === seasonTypeDateRange) {
                      <input
                        type="text"
                        class="form-control form-control-sm mb-1"
                        [value]="rate.season_description ?? ''"
                        (input)="updateEnergyRateField($index, 'season_description', $any($event.target).value)"
                        placeholder="e.g. Summer, Winter" />
                      @if (expandedEnergySeasonRowIndex() === $index) {
                        <div class="mb-1">
                          <app-datepicker
                            [ngModel]="getEnergySeasonRange($index)"
                            (ngModelChange)="onEnergySeasonRangeChange($index, $event)"
                            [range]="true"
                            [displayMonths]="2"
                            [hideNavigation]="false" />
                          <button
                            type="button"
                            class="btn btn-outline-secondary btn-sm mt-1"
                            (click)="toggleEnergySeasonDatepickerExpanded($index)">
                            Done
                          </button>
                        </div>
                      } @else {
                        <div class="d-flex align-items-center gap-1 flex-wrap">
                          <span class="text-muted small">{{ formatSeasonRangeDisplay(rate) }}</span>
                          <button
                            type="button"
                            class="btn btn-outline-secondary btn-sm py-0"
                            (click)="toggleEnergySeasonDatepickerExpanded($index)">
                            {{ getEnergySeasonRange($index)?.fromDate && getEnergySeasonRange($index)?.toDate ? 'Change dates' : 'Select dates' }}
                          </button>
                        </div>
                      }
                    }
                  </td>
                  <td>
                    <input
                      type="number"
                      class="form-control form-control-sm"
                      [class.is-invalid]="!isEnergyRateValueValid(rate)"
                      [value]="rate.energy_rate_kwh ?? ''"
                      (input)="updateEnergyRateField($index, 'energy_rate_kwh', $any($event.target).value)"
                      placeholder="0 (required)"
                      step="0.01" />
                    @if (!isEnergyRateValueValid(rate)) {
                      <div class="invalid-feedback d-block">Rate is required (positive or negative number).</div>
                    }
                  </td>
                  <td>
                    <input
                      type="number"
                      class="form-control form-control-sm"
                      [value]="rate.min_kv ?? ''"
                      (input)="updateEnergyRateField($index, 'min_kv', $any($event.target).value)"
                      placeholder="-"
                      step="0.1"
                      min="0" />
                  </td>
                  <td>
                    <input
                      type="number"
                      class="form-control form-control-sm"
                      [value]="rate.max_kv ?? ''"
                      (input)="updateEnergyRateField($index, 'max_kv', $any($event.target).value)"
                      placeholder="-"
                      step="0.1"
                      min="0" />
                  </td>
                  <td>
                    <div class="d-flex align-items-center gap-1">
                      <input
                        type="number"
                        class="form-control form-control-sm"
                        [value]="rate.start_kwh ?? ''"
                        (input)="updateEnergyRateField($index, 'start_kwh', $any($event.target).value)"
                        placeholder="Start"
                        step="1"
                        min="0" />
                      <span class="text-muted">-</span>
                      <input
                        type="number"
                        class="form-control form-control-sm"
                        [value]="rate.end_kwh ?? ''"
                        (input)="updateEnergyRateField($index, 'end_kwh', $any($event.target).value)"
                        placeholder="End"
                        step="1"
                        min="0" />
                    </div>
                  </td>
                  <td>
                    <input
                      type="text"
                      class="form-control form-control-sm"
                      [value]="rate.start_time ?? ''"
                      (input)="updateEnergyRateField($index, 'start_time', $any($event.target).value)"
                      placeholder="HH:MM" />
                  </td>
                  <td>
                    <input
                      type="text"
                      class="form-control form-control-sm"
                      [value]="rate.end_time ?? ''"
                      (input)="updateEnergyRateField($index, 'end_time', $any($event.target).value)"
                      placeholder="HH:MM" />
                  </td>
                  <td>
                    <select
                      class="form-select form-select-sm"
                      [ngModel]="getAppliesValue(rate.days_of_week_holidays)"
                      (ngModelChange)="updateEnergyRateField($index, 'days_of_week_holidays', $event)">
                      <option value="">—</option>
                      @for (opt of appliesOptions; track opt.value) {
                        <option [value]="opt.value">{{ opt.label }}</option>
                      }
                    </select>
                  </td>
                  <td class="text-start">
                    <div class="d-flex align-items-center justify-content-start gap-1">
                      <button
                        type="button"
                        class="btn btn-outline-danger btn-sm py-0 px-1"
                        (click)="deleteEnergyRateRow($index)"
                        title="Delete row">
                        <i class="bi bi-trash" aria-label="Delete row"></i>
                      </button>
                      @if (rate.manually_entered) {
                        <i class="bi bi-pencil-square text-secondary" title="Manually entered" aria-label="Manually entered"></i>
                      }
                    </div>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
    </div>
    <div viewContent>
        @if (energyRates.length > 0) {
          <div class="table-responsive">
            <table class="table table-sm table-striped">
              <thead>
                <tr>
                  <th>Rate Description</th>
                  <th>Season</th>
                  <th>Rate ($/kWh)</th>
                  <th>Min/Max kV</th>
                  <th>kWh Range</th>
                  <th>Time Period</th>
                  <th>Applies</th>
                  <th class="text-end" style="width: 3.5rem;" aria-label="Manually entered"></th>
                </tr>
              </thead>
              <tbody>
                @for (seasonGroup of energyRatesBySeason; track seasonGroup.season) {
                  @for (rate of seasonGroup.rates; track rate.id) {
                    @let seasonTooltip = getSeasonTooltipText(rate);
                    <tr>
                      <td>{{ rate.rate_description || '-' }}</td>
                      <td>
                        <span>{{ rate.season_description || '-' }}</span>
                        @if (seasonTooltip) {
                          <span class="season-info-icon ms-1">
                            <button type="button" class="season-info-button" aria-label="Show season date range">
                              <i class="bi bi-info-circle"></i>
                              <span class="season-tooltip" role="tooltip">{{ seasonTooltip }}</span>
                            </button>
                          </span>
                        }
                      </td>
                      <td>{{ rate.energy_rate_kwh || '-' }}</td>
                      <td>
                        @if (rate.min_kv || rate.max_kv) {
                          {{ rate.min_kv || 'N/A' }} - {{ rate.max_kv || 'N/A' }}
                        } @else {
                          -
                        }
                      </td>
                      <td>{{ getEnergyRangeText(rate) }}</td>
                      <td>
                        @if (rate.start_time || rate.end_time) {
                          {{ rate.start_time || 'N/A' }} - {{ rate.end_time || 'N/A' }}
                        } @else {
                          -
                        }
                      </td>
                      <td>{{ getAppliesText(rate.days_of_week_holidays) }}</td>
                      <td class="text-end">
                        @if (rate.manually_entered) {
                          <i class="bi bi-pencil-square text-secondary" title="Manually entered" aria-label="Manually entered"></i>
                        }
                      </td>
                    </tr>
                  }
                }
              </tbody>
            </table>
          </div>
        }
    </div>
  </app-editable-table-section>

  <!-- Schedule Metadata -->
  @if (scheduleMetadataValue) {
    <!-- Service Charges: always show section when metadata is available (empty or not) -->
    <app-editable-table-section
      sectionTitle="Service Charges"
      [expanded]="showServiceCharges()"
      [showEditButton]="!readOnly"
      [editing]="editingServiceCharges()"
      [isSaving]="isSavingServiceCharges"
      [saveDisabled]="!isServiceChargesEditValid()"
      editButtonTitle="Edit service charges"
      [hasRows]="(editingServiceCharges() ? serviceChargesEdit().length : serviceCharges.length) > 0"
      [hasRowsMarkedForDelete]="false"
      emptyMessage="No rows. Click &quot;Add row&quot; to add a service charge."
      [viewEmptyMessage]="'No service charges. Click Edit to add a service charge.'"
      (toggle)="toggleServiceCharges()"
      (enterEdit)="enterServiceChargesEdit()"
      (cancelEdit)="cancelServiceChargesEdit()"
      (addRow)="addServiceChargeRow()"
      (save)="saveServiceChargesEdit()">
      <div editContent>
          <div class="table-responsive">
            <table class="table table-sm table-striped">
              <thead>
                <tr>
                  <th>Description</th>
                  <th>Rate ($)</th>
                  <th>Charge Unit</th>
                  <th class="text-start" style="width: 3.5rem;" aria-label="Actions"></th>
                </tr>
              </thead>
              <tbody>
                @for (charge of serviceChargesEdit(); track $index) {
                  <tr>
                    <td>
                      <input
                        type="text"
                        class="form-control form-control-sm"
                        [class.is-invalid]="!isServiceChargeDescriptionValid(charge)"
                        [value]="charge.Description"
                        (input)="updateServiceChargeField($index, 'Description', $any($event.target).value)"
                        placeholder="Description" />
                    </td>
                    <td>
                      <input
                        type="text"
                        class="form-control form-control-sm"
                        [class.is-invalid]="!isServiceChargeRateValid(charge)"
                        [value]="charge.Rate"
                        (input)="updateServiceChargeField($index, 'Rate', $any($event.target).value)"
                        placeholder="0.00" />
                    </td>
                    <td>
                      <select
                        class="form-select form-select-sm"
                        [ngModel]="charge.ChargeUnit"
                        (ngModelChange)="updateServiceChargeField($index, 'ChargeUnit', $event)">
                        @for (opt of serviceChargeUnitOptions; track opt.value) {
                          <option [value]="opt.value">{{ opt.label }}</option>
                        }
                      </select>
                    </td>
                    <td class="text-start">
                      <div class="d-flex align-items-center justify-content-start gap-1">
                        <button
                          type="button"
                          class="btn btn-outline-danger btn-sm py-0 px-1"
                          (click)="deleteServiceChargeRow($index)"
                          title="Delete row">
                          <i class="bi bi-trash" aria-label="Delete row"></i>
                        </button>
                        @if (charge.manually_entered) {
                          <i class="bi bi-pencil-square text-secondary" title="Manually entered" aria-label="Manually entered"></i>
                        }
                      </div>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
      </div>
      <div viewContent>
          <div class="table-responsive">
            <table class="table table-sm table-striped">
              <thead>
                <tr>
                  <th>Description</th>
                  <th>Rate ($)</th>
                  <th>Charge Unit</th>
                  <th class="text-end" style="width: 3.5rem;" aria-label="Manually entered"></th>
                </tr>
              </thead>
              <tbody>
                @for (charge of serviceCharges; track charge.ScheduleID + charge.Description + $index) {
                  <tr>
                    <td>{{ charge.Description }}</td>
                    <td>{{ charge.Rate }}</td>
                    <td>{{ charge.ChargeUnit }}</td>
                    <td class="text-end">
                      @if (charge.manually_entered) {
                        <i class="bi bi-pencil-square text-secondary" title="Manually entered" aria-label="Manually entered"></i>
                      }
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
      </div>
    </app-editable-table-section>

    <!-- Other Charges: always show when metadata is available -->
    <app-editable-table-section
      sectionTitle="Other Charges"
      [expanded]="showOtherCharges()"
      [showEditButton]="!readOnly"
      [editing]="editingOtherCharges()"
      [isSaving]="isSavingOtherCharges"
      [saveDisabled]="!isOtherChargesEditValid()"
      editButtonTitle="Edit other charges"
      [hasRows]="(editingOtherCharges() ? otherChargesEdit().length : otherCharges.length) > 0"
      [hasRowsMarkedForDelete]="false"
      emptyMessage="No rows. Click &quot;Add row&quot; to add an other charge."
      [viewEmptyMessage]="'No other charges. Click Edit to add an other charge.'"
      (toggle)="toggleOtherCharges()"
      (enterEdit)="enterOtherChargesEdit()"
      (cancelEdit)="cancelOtherChargesEdit()"
      (addRow)="addOtherChargeRow()"
      (save)="saveOtherChargesEdit()">
      <div editContent>
          <div class="table-responsive">
            <table class="table table-sm table-striped">
              <thead>
                <tr>
                  <th>Description</th>
                  <th>Charge Type ($)</th>
                  <th>Charge Unit</th>
                  <th class="text-start" style="width: 3.5rem;" aria-label="Actions"></th>
                </tr>
              </thead>
              <tbody>
                @for (charge of otherChargesEdit(); track $index) {
                  <tr>
                    <td>
                      <input
                        type="text"
                        class="form-control form-control-sm"
                        [class.is-invalid]="!isOtherChargeDescriptionValid(charge)"
                        [value]="charge.Description"
                        (input)="updateOtherChargeField($index, 'Description', $any($event.target).value)"
                        placeholder="Description" />
                    </td>
                    <td>
                      <input
                        type="text"
                        class="form-control form-control-sm"
                        [class.is-invalid]="!isOtherChargeTypeValid(charge)"
                        [value]="charge.ChargeType"
                        (input)="updateOtherChargeField($index, 'ChargeType', $any($event.target).value)"
                        placeholder="0.00" />
                    </td>
                    <td>
                      <select
                        class="form-select form-select-sm"
                        [ngModel]="charge.ChargeUnit"
                        (ngModelChange)="updateOtherChargeField($index, 'ChargeUnit', $event)">
                        @for (opt of serviceChargeUnitOptions; track opt.value) {
                          <option [value]="opt.value">{{ opt.label }}</option>
                        }
                      </select>
                    </td>
                    <td class="text-start">
                      <div class="d-flex align-items-center justify-content-start gap-1">
                        <button
                          type="button"
                          class="btn btn-outline-danger btn-sm py-0 px-1"
                          (click)="deleteOtherChargeRow($index)"
                          title="Delete row">
                          <i class="bi bi-trash" aria-label="Delete row"></i>
                        </button>
                        @if (charge.manually_entered) {
                          <i class="bi bi-pencil-square text-secondary" title="Manually entered" aria-label="Manually entered"></i>
                        }
                      </div>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
      </div>
      <div viewContent>
          <div class="table-responsive">
            <table class="table table-sm table-striped">
              <thead>
                <tr>
                  <th>Description</th>
                  <th>Charge Type ($)</th>
                  <th>Charge Unit</th>
                  <th class="text-end" style="width: 3.5rem;" aria-label="Manually entered"></th>
                </tr>
              </thead>
              <tbody>
                @for (charge of otherCharges; track charge.ScheduleID + charge.Description + $index) {
                  <tr>
                    <td>{{ charge.Description }}</td>
                    <td>{{ charge.ChargeType }}</td>
                    <td>{{ charge.ChargeUnit }}</td>
                    <td class="text-end">
                      @if (charge.manually_entered) {
                        <i class="bi bi-pencil-square text-secondary" title="Manually entered" aria-label="Manually entered"></i>
                      }
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
      </div>
    </app-editable-table-section>

    <!-- Notes: always show when metadata is available -->
    <app-editable-table-section
      sectionTitle="Notes"
      [expanded]="showNotes()"
      [showEditButton]="!readOnly"
      [editing]="editingNotes()"
      [isSaving]="isSavingNotes"
      [saveDisabled]="!isNotesEditValid()"
      editButtonTitle="Edit notes"
      [hasRows]="(editingNotes() ? notesEdit().length : notes.length) > 0"
      [hasRowsMarkedForDelete]="false"
      emptyMessage="No notes. Click &quot;Add row&quot; to add a note."
      [viewEmptyMessage]="'No notes. Click Edit to add a note.'"
      (toggle)="toggleNotes()"
      (enterEdit)="enterNotesEdit()"
      (cancelEdit)="cancelNotesEdit()"
      (addRow)="addNoteRow()"
      (save)="saveNotesEdit()">
      <div editContent>
          <div class="table-responsive">
            <table class="table table-sm table-striped">
              <thead>
                <tr>
                  <th>Description</th>
                  <th>Type</th>
                  <th class="text-start" style="width: 3.5rem;" aria-label="Actions"></th>
                </tr>
              </thead>
              <tbody>
                @for (note of notesEdit(); track $index) {
                  <tr>
                    <td>
                      <input
                        type="text"
                        class="form-control form-control-sm"
                        [class.is-invalid]="!isNoteDescriptionValid(note)"
                        [value]="note.Description"
                        (input)="updateNoteField($index, 'Description', $any($event.target).value)"
                        placeholder="Note text" />
                    </td>
                    <td>
                      <input
                        type="text"
                        class="form-control form-control-sm"
                        [value]="note.Type ?? ''"
                        (input)="updateNoteField($index, 'Type', $any($event.target).value)"
                        placeholder="Type (optional)" />
                    </td>
                    <td class="text-start">
                      <div class="d-flex align-items-center justify-content-start gap-1">
                        <button
                          type="button"
                          class="btn btn-outline-danger btn-sm py-0 px-1"
                          (click)="deleteNoteRow($index)"
                          title="Delete row">
                          <i class="bi bi-trash" aria-label="Delete row"></i>
                        </button>
                        @if (note.manually_entered) {
                          <i class="bi bi-pencil-square text-secondary" title="Manually entered" aria-label="Manually entered"></i>
                        }
                      </div>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
      </div>
      <div viewContent>
          <div class="table-responsive">
            <table class="table table-sm table-striped">
              <thead>
                <tr>
                  <th>Description</th>
                  <th class="text-end" style="width: 3.5rem;" aria-label="Manually entered"></th>
                </tr>
              </thead>
              <tbody>
                @for (note of notes; track note.ScheduleID + note.Description + $index) {
                  <tr>
                    <td>
                      @if (note.Type) {
                        <strong>{{ note.Type }}: </strong>
                      }
                      {{ note.Description }}
                    </td>
                    <td class="text-end">
                      @if (note.manually_entered) {
                        <i class="bi bi-pencil-square text-secondary" title="Manually entered" aria-label="Manually entered"></i>
                      }
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
      </div>
    </app-editable-table-section>
  }
}

